import React, { useState, useMemo, useEffect } from 'react';
import { useTimeClock } from '../hooks/useTimeClock';
import { PayrollFrequency, PayrollSettings, TimeSettings, TimeRounding, DayOfWeek, PinpadSize, CompanySettings } from '../../shared/types.js';
import { calculatePeriodDates, toDateInputString, cloneDeep } from '../utils.js';
import Button from './Button';
import { QuestionMarkCircleIcon, LockIcon, UnlockIcon } from './icons';
import ReopenPeriodModal from './ReopenPeriodModal';
import ClosePeriodModal from './ClosePeriodModal';

const Fieldset: React.FC<{ legend: string; children: React.ReactNode; className?: string }> = ({ legend, children, className }) => (
    <div className={`border border-gray-200 dark:border-gray-700 rounded-lg p-4 pt-2 ${className}`}>
        <legend className="px-2 font-semibold text-gray-700 dark:text-gray-200 text-lg">{legend}</legend>
        <div className="pt-2 space-y-3">{children}</div>
    </div>
);

const RadioButton: React.FC<{
    name: string;
    value: string;
    checked: boolean;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    label: string;
    description: string;
    disabled?: boolean;
}> = ({ name, value, checked, onChange, label, description, disabled }) => (
     <label className={`flex items-start gap-3 p-2 rounded-md transition-colors ${disabled ? 'opacity-60 cursor-not-allowed' : 'hover:bg-gray-100 dark:hover:bg-gray-700/50 cursor-pointer'}`}>
        <input type="radio" name={name} value={value} checked={checked} onChange={onChange} disabled={disabled} className="mt-1 h-4 w-4 text-brand-secondary focus:ring-brand-secondary bg-gray-200 dark:bg-gray-600 border-gray-300 dark:border-gray-500" />
        <div>
            <span className="font-medium text-gray-900 dark:text-white">{label}</span>
            <p className="text-sm text-gray-500 dark:text-gray-400">{description}</p>
        </div>
    </label>
);

const PayrollSettingsTab: React.FC = () => {
    const { 
        payrollSettings, timeSettings, companySettings, updatePayrollSettings, 
        updateTimeSettings, updateCompanySettings, reopenPeriod, closePeriod,
        pinpadSize, updatePinpadSize
    } = useTimeClock();
    const [isReopenModalOpen, setIsReopenModalOpen] = useState(false);
    const [isClosePeriodModalOpen, setIsClosePeriodModalOpen] = useState(false);
    const [localPayrollSettings, setLocalPayrollSettings] = useState<PayrollSettings | null>(cloneDeep(payrollSettings));
    const [localTimeSettings, setLocalTimeSettings] = useState<TimeSettings | null>(cloneDeep(timeSettings));
	const [localCompanySettings, setLocalCompanySettings] = useState<CompanySettings | null>(cloneDeep(companySettings));
    useEffect(() => {
        setLocalPayrollSettings(cloneDeep(payrollSettings));
    }, [payrollSettings]);
    useEffect(() => {
        setLocalTimeSettings(cloneDeep(timeSettings));
    }, [timeSettings]);
	useEffect(() => {
        setLocalCompanySettings(cloneDeep(companySettings));
    }, [companySettings]);
    const isDirty = useMemo(() => {
        return JSON.stringify(payrollSettings) !== JSON.stringify(localPayrollSettings) ||
               JSON.stringify(timeSettings) !== JSON.stringify(localTimeSettings) ||
			   JSON.stringify(companySettings) !== JSON.stringify(localCompanySettings);
    }, [payrollSettings, localPayrollSettings, timeSettings, companySettings, localTimeSettings, localCompanySettings]);
    const handleApply = () => {
        if (localPayrollSettings) updatePayrollSettings(localPayrollSettings);
        if (localTimeSettings) updateTimeSettings(localTimeSettings);
		if (localCompanySettings) updateCompanySettings(localCompanySettings);
    };
    const handleRevert = () => {
        setLocalPayrollSettings(cloneDeep(payrollSettings));
        setLocalTimeSettings(cloneDeep(timeSettings));
		setLocalCompanySettings(cloneDeep(companySettings));
    };
    const isEmployeeDefined = localPayrollSettings?.frequency === PayrollFrequency.EMPLOYEE_DEFINED;
    const calculatedEndDate = useMemo(() => {
        if (!localPayrollSettings?.startDate) return '';
        try {
            const { end } = calculatePeriodDates(localPayrollSettings);
            return toDateInputString(end);
        } catch(e) { return ''; }
    }, [localPayrollSettings]);
    const handleFrequencyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newFreq = e.target.value as PayrollFrequency;
        setLocalPayrollSettings(prev => prev ? { ...prev, frequency: newFreq } : null);
    };
    const handleDateChange = (field: 'startDate' | 'endDate', value: string) => {
        if (field === 'startDate' && localPayrollSettings?.isStartDateLocked) {
            return;
        }
        setLocalPayrollSettings(prev => prev ? { ...prev, [field]: value } : null);
    };
    const handleReopenConfirm = async () => {
        await reopenPeriod();
        setIsReopenModalOpen(false);
        alert('Period reopened successfully.');
    };
    const handleClosePeriodConfirm = async () => {
        await closePeriod();
        setIsClosePeriodModalOpen(false);
        alert('Period closed successfully. The new payroll period has started.');
    };
    const handleTimeSettingsUpdate = <K extends keyof TimeSettings>(key: K, value: TimeSettings[K]) => {
        setLocalTimeSettings(prev => prev ? ({ ...prev, [key]: value }) : null);
    };
	const handleCompanySettingsUpdate = <K extends keyof CompanySettings>(key: K, value: CompanySettings[K]) => {
        setLocalCompanySettings(prev => prev ? ({...prev, [key]: value}) : null);
    };
    const inputClasses = "block w-full bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-brand-secondary focus:border-brand-secondary text-gray-900 dark:text-white";
    const disabledInputClasses = `${inputClasses} bg-gray-200 dark:bg-gray-900/50 cursor-not-allowed`;
    
    if (!localPayrollSettings || !localTimeSettings || !localCompanySettings) {
        return <div>Loading settings...</div>;
    }
    return (
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg max-w-5xl mx-auto text-gray-900 dark:text-white">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-8">
                    <Fieldset legend="Company Name">
						<input
							type="text"
							id="companyName"
							value={localCompanySettings.companyName}
							onChange={(e) => handleCompanySettingsUpdate('companyName', e.target.value)}
							className={inputClasses}
						/>
						 <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">This will be displayed on reports.</p>
					</Fieldset>
					<Fieldset legend="Company Address">
						<textarea
							id="companyAddress"
							rows={3}
							value={localCompanySettings.companyAddress}
							onChange={(e) => handleCompanySettingsUpdate('companyAddress', e.target.value)}
							className={inputClasses}
						/>
						 <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">This will be displayed on reports.</p>
					</Fieldset>
                    <Fieldset legend="Pay Period Configuration">
                         <div className="space-y-4">
                            <div>
								<label htmlFor="payroll-frequency" className="flex items-center gap-2 text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                                    <span>Payroll Frequency</span>
                                    {localPayrollSettings.isStartDateLocked && <LockIcon className="w-4 h-4 text-yellow-500" />}
                                </label>
								<select
									id="payroll-frequency"
									value={localPayrollSettings.frequency}
									onChange={handleFrequencyChange}
									required
									className={localPayrollSettings.isStartDateLocked ? disabledInputClasses : inputClasses}
									disabled={localPayrollSettings.isStartDateLocked}
									title={localPayrollSettings.isStartDateLocked ? "The Payroll Frequency is locked after the initial setup wizard is complete." : ""}
								>
									{Object.values(PayrollFrequency).map(freq => (
										<option key={freq} value={freq}>{freq}</option>
									))}
								</select>
							</div>
							<div>
                                <label htmlFor="start-date" className="flex items-center gap-2 text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                                    <span>Start Date</span>
                                    {localPayrollSettings.isStartDateLocked && <LockIcon className="w-4 h-4 text-yellow-500" />}
                                </label>
                                <input
                                    id="start-date"
                                    type="date"
                                    value={localPayrollSettings.startDate || ''}
                                    onChange={e => handleDateChange('startDate', e.target.value)}
                                    required
                                    className={localPayrollSettings.isStartDateLocked ? disabledInputClasses : inputClasses}
                                    disabled={localPayrollSettings.isStartDateLocked}
                                    title={localPayrollSettings.isStartDateLocked ? "The start date is locked after the initial setup wizard is complete." : ""}
                                />
                                {localPayrollSettings.frequency === PayrollFrequency.SEMI_MONTHLY && (
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">For Semi-Monthly, please select the 1st or 16th of the month.</p>
                                )}
                            </div>
                            <div>
                                <label htmlFor="end-date" className="flex items-center gap-2 text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
									<span>End Date</span>
									{(payrollSettings?.isStartDateLocked || !isEmployeeDefined) && (<LockIcon className="w-4 h-4 text-yellow-500" />)}
								</label>
                                <input
									id="end-date"
									type="date"
									value={isEmployeeDefined ? (localPayrollSettings.endDate || '') : calculatedEndDate}
									onChange={e => handleDateChange('endDate', e.target.value)}
									required
									readOnly={!isEmployeeDefined}
									className={isEmployeeDefined ? inputClasses : disabledInputClasses}
								/>
								<p className="text-xs text-gray-500 dark:text-gray-400 mt-1">The end date of the payroll period. This is calculated automatically unless 'Employee Defined' frequency is selected.</p>
                            </div>
                         </div>
                         {payrollSettings?.isStartDateLocked && (
                            <div className="mt-4 p-3 bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300 rounded-md border border-yellow-200 dark:border-yellow-800">
                                <label className="flex items-start gap-3 cursor-pointer">
                                    <input 
                                        type="checkbox" 
                                        checked={!localPayrollSettings.isStartDateLocked}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                if (confirm("WARNING: Unlocking these settings can have significant impacts on payroll calculations. Are you sure you want to proceed?")) {
                                                    setLocalPayrollSettings(prev => prev ? ({ ...prev, isStartDateLocked: false }) : null);
                                                }
                                            }
                                        }}
                                        className="mt-1 h-4 w-4 text-yellow-600 focus:ring-yellow-500 bg-gray-200 dark:bg-gray-600 border-gray-300 dark:border-gray-500 rounded"
                                    />
                                    <div>
                                        <span className="font-bold">Unlock Payroll Settings</span>
                                        <p className="text-xs">
                                            This allows you to change the Payroll Frequency and Start Date. This is an advanced action and should only be done if you are certain it is necessary. The settings will be permanently unlocked upon applying this change.
                                        </p>
                                    </div>
                                </label>
                            </div>
                        )}
                         <div className="flex flex-col sm:flex-row gap-4 pt-4 mt-4 border-t border-gray-200 dark:border-gray-700 justify-center">
                                <Button type="button" variant="secondary" onClick={() => setIsClosePeriodModalOpen(true)} className="w-full sm:w-auto">
                                    <LockIcon className="w-5 h-5"/> Close Period
                                </Button>
                                <Button type="button" variant="warning" onClick={() => setIsReopenModalOpen(true)} className="w-full sm:w-auto">
                                    <UnlockIcon className="w-5 h-5"/> Reopen Period
                                </Button>
                            </div>
                    </Fieldset>
                </div>
                <div className="space-y-8">
                     <Fieldset legend="Reports Time & Overtime">
                        <div className="grid grid-cols-2 items-center gap-4">
                            <label htmlFor="workweek-starts">Workweek starts on</label>
                            <select
                                id="workweek-starts"
                                value={localTimeSettings.workweekStartsWith}
                                onChange={(e) => handleTimeSettingsUpdate('workweekStartsWith', e.target.value as DayOfWeek)}
                                className={`${inputClasses} py-2 px-3`}
                            >
                                {Object.values(DayOfWeek).map(day => <option key={day} value={day}>{day}</option>)}
                            </select>
                        </div>
                        <label className="flex items-center gap-2 cursor-pointer">
                             <input type="checkbox" checked={localTimeSettings.includePaidLeaveInOT} onChange={e => handleTimeSettingsUpdate('includePaidLeaveInOT', e.target.checked)} className="w-4 h-4 bg-gray-200 dark:bg-gray-600 border-gray-300 dark:border-gray-500 rounded text-brand-secondary focus:ring-brand-secondary" />
                             <span>Include paid leave in weekly OT</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                             <input type="checkbox" checked={localTimeSettings.includePastMidnight} onChange={e => handleTimeSettingsUpdate('includePastMidnight', e.target.checked)} className="w-4 h-4 bg-gray-200 dark:bg-gray-600 border-gray-300 dark:border-gray-500 rounded text-brand-secondary focus:ring-brand-secondary" />
                             <span>Include hours past midnight with prior day</span>
                        </label>
                    </Fieldset>
                     <Fieldset legend="Rounding Time Worked">
                         <RadioButton
                            name="rounding"
                            value={TimeRounding.NONE}
                            checked={localTimeSettings.rounding === TimeRounding.NONE}
                            onChange={(e) => handleTimeSettingsUpdate('rounding', e.target.value as TimeRounding)}
                            label="None" description=""
                        />
                         <RadioButton
                            name="rounding"
                            value={TimeRounding.TENTH_HOUR}
                            checked={localTimeSettings.rounding === TimeRounding.TENTH_HOUR}
                            onChange={(e) => handleTimeSettingsUpdate('rounding', e.target.value as TimeRounding)}
                            label="Tenth Hour" description=""
                        />
                         <RadioButton
                            name="rounding"
                            value={TimeRounding.QUARTER_HOUR}
                            checked={localTimeSettings.rounding === TimeRounding.QUARTER_HOUR}
                            onChange={(e) => handleTimeSettingsUpdate('rounding', e.target.value as TimeRounding)}
                            label="Quarter Hour" description=""
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">The start and stop times are rounded when totalling time worked.</p>
                    </Fieldset>

                    <Fieldset legend="Pinpad Size">
                         <div className="grid grid-cols-2 items-center gap-4">
                            <label htmlFor="pinpad-size" className="font-medium">Size</label>
                            <select
                                id="pinpad-size"
                                value={pinpadSize}
                                onChange={(e) => updatePinpadSize(e.target.value as PinpadSize)}
                                className={`${inputClasses} py-2 px-3`}
                            >
                                {Object.values(PinpadSize).map(size => <option key={size} value={size}>{size}</option>)}
                            </select>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Set the size for the PIN entry pads. This setting is device-specific.</p>
                    </Fieldset>
                </div>
            </div>
             <div className="flex justify-end gap-4 pt-8 mt-8 border-t border-gray-200 dark:border-gray-700">
                <Button variant="secondary" onClick={handleRevert} disabled={!isDirty}>Revert</Button>
                <Button onClick={handleApply} disabled={!isDirty}>Apply</Button>
            </div>
             <ClosePeriodModal
                isOpen={isClosePeriodModalOpen}
                onClose={() => setIsClosePeriodModalOpen(false)}
                onConfirm={handleClosePeriodConfirm}
                payrollSettings={localPayrollSettings}
            />
             <ReopenPeriodModal
                isOpen={isReopenModalOpen}
                onClose={() => setIsReopenModalOpen(false)}
                onConfirm={handleReopenConfirm}
                payrollSettings={localPayrollSettings}
            />
        </div>
    );
};

export default PayrollSettingsTab;